/**
 * GradientOrb — absolutely-positioned blurred animated radial gradient.
 * Decorative only (aria-hidden). Used for ambient color in dark sections.
 *
 * @spec /doc/specs/frontend/design-system/design-tokens-v2.md
 * @userStory US-EP022-011
 */

import { cn } from '@/lib/utils';

type Position = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center';
type OrbSize = 'sm' | 'md' | 'lg' | 'xl';
type Intensity = 'low' | 'medium' | 'high';
type OrbColor = 'cyan' | 'pink';

const POSITION_CLASSES: Record<Position, string> = {
  'top-left': 'top-[-15%] left-[-10%]',
  'top-right': 'top-[-15%] right-[-10%]',
  'bottom-left': 'bottom-[-15%] left-[-10%]',
  'bottom-right': 'bottom-[-15%] right-[-10%]',
  center: 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
};

const SIZE_CLASSES: Record<OrbSize, string> = {
  sm: 'w-[280px] h-[280px]',
  md: 'w-[420px] h-[420px]',
  lg: 'w-[640px] h-[640px]',
  xl: 'w-[920px] h-[920px]',
};

const INTENSITY_CLASSES: Record<Intensity, string> = {
  low: 'opacity-25',
  medium: 'opacity-45',
  high: 'opacity-65',
};

const COLOR_GRADIENT: Record<OrbColor, string> = {
  cyan: 'radial-gradient(circle at 30% 30%, rgba(26,171,255,.65) 0%, rgba(26,171,255,0) 70%)',
  pink: 'radial-gradient(circle at 30% 30%, rgba(236,72,153,.55) 0%, rgba(236,72,153,0) 70%)',
};

export interface GradientOrbProps {
  position?: Position;
  size?: OrbSize;
  intensity?: Intensity;
  color?: OrbColor;
  className?: string;
}

export function GradientOrb({
  position = 'top-right',
  size = 'lg',
  intensity = 'medium',
  color = 'cyan',
  className,
}: GradientOrbProps) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        'pointer-events-none absolute animate-orb-drift rounded-full blur-3xl',
        POSITION_CLASSES[position],
        SIZE_CLASSES[size],
        INTENSITY_CLASSES[intensity],
        className
      )}
      style={{ background: COLOR_GRADIENT[color] }}
    />
  );
}
